<template>
	<div class="summary_table_container" :class="{ is_refreshing: isLoading && rows.length }">
		<table class="summary_table">
			<colgroup>
				<col class="col_label">
				<col v-for="column in columns" :key="column.key" :class="column.colClass">
			</colgroup>
			<thead>
				<tr>
					<th scope="col" class="sticky_column header_corner">{{ cornerLabel }}</th>
					<th v-for="column in columns" :key="column.key" scope="col"
						class="has-text-right nobreak" :class="column.headClass">
						<span v-if="column.key === 'month' && isCurrentMonth"
							v-tooltip.top="'Mes en curso: los datos aún se están cargando.'">
							{{ column.label }}
						</span>
						<template v-else>{{ column.label }}</template>
					</th>
				</tr>
			</thead>
			<tbody v-if="rows.length">
				<tr v-for="row in visibleRows" :key="row.key"
					:class="[row.theme || 'theme_row_white', `depth_${depthOf(row)}`, { is_emphasis: row.emphasis, is_child: row.parentKey, navigable: isClickable(row) }]"
					@click="onRowClick(row)">
					<th scope="row" class="sticky_column row_label">
						<!-- Nombre + chevron en las filas con cuentas (Ventas, Gastos
							Totales, No Operacional): el chevron pliega y despliega sin
							disparar el clic de la fila. -->
						<div class="label_cell">
							<!-- Nombre en una línea con «…» (CSS) y el completo en el
								tooltip cuando no cabe: la primera columna es fija y la
								tabla no cambia de ancho al desplegar cuentas de nombre
								largo (Steve, 2026-10-07). -->
							<span class="row_label_text" v-tooltip.right="labelTooltip(row)">{{ row.label }}</span>
							<button v-if="row.hasChilds"
								type="button"
								class="row_chevron"
								:aria-expanded="isExpanded(row) ? 'true' : 'false'"
								:aria-label="isExpanded(row) ? 'Plegar cuentas' : 'Desplegar cuentas'"
								@click.stop="$emit('toggle-childs', row.key)">
								<ArrowDown class="row_chevron_icon" :class="{ 'is-rotated': isExpanded(row) }" />
							</button>
						</div>
					</th>
					<td v-for="column in columns" :key="column.key"
						class="has-text-right nobreak" :class="column.cellClass">
						<template v-if="column.delta">
							<span class="delta" :class="`tone_${deltaTone(row.deltas[column.delta], row.polarity)}`">
								{{ formatDelta(row.deltas[column.delta], { isMoney: row.isMoney }) }}
							</span>
						</template>
						<template v-else-if="row.values[column.value] == null">
							<span class="empty_value" v-tooltip.top="emptyTooltip">{{ EMPTY_VALUE }}</span>
						</template>
						<template v-else-if="row.isMoney">
							<span class="cell_value" v-tooltip.top="parseToMoney(row.values[column.value])">
								{{ parseToCompactMoney(row.values[column.value]) }}
							</span>
						</template>
						<template v-else>{{ formatPercent(row.values[column.value]) }}</template>
					</td>
				</tr>
			</tbody>
			<!-- Esqueleto con la forma final: la pantalla no salta cuando llegan
				los datos. Sólo en la primera carga — al cambiar de mes se conserva
				el contenido anterior atenuado (is_refreshing). -->
			<tbody v-else-if="isLoading" class="skeleton">
				<tr v-for="index in skeletonRows" :key="index">
					<th scope="row" class="sticky_column"><span class="skeleton_bar label_bar"></span></th>
					<td v-for="column in columns" :key="column.key" :class="column.cellClass">
						<span class="skeleton_bar"></span>
					</td>
				</tr>
			</tbody>
		</table>
	</div>
</template>

<script>
import moment from "moment";
import ArrowDown from "@/components/svg/ArrowDown.vue";
import { parseToCompactMoney, parseToMoney } from "@/utils/currency";
import { formatPercent } from "@/views/financialReports/utils/periodFormatting";
import { EMPTY_VALUE, deltaTone, formatDelta } from "@/utils/dashboardDelta";

const SKELETON_ROWS = 17;

// Tabla resumen del tab Dashboard: el mes focal contra el mes anterior, el
// mismo mes del año anterior y los acumulados del año (YTD) con sus
// variaciones. Recibe las filas ya normalizadas por el store
// (`dashboardReport.normalizeDashboard`) y no toca Vuex: la polaridad, el
// tema de fila y el tab destino vienen del backend. Montos en formato
// compacto (D1) con el monto completo en el tooltip; % enteros.
//
// Filas plegables (Steve, 2026-10-06 y 07), con la jerarquía del Resumen: un
// total despliega sus líneas y una línea sus cuentas de nivel 3 (hasta dos
// niveles bajo un total). Una fila con `hasChilds` lleva un chevron; una
// fila con `parentKey` sólo se muestra si TODA su cadena de padres está en
// `expandedRows`, que maneja el tab (arranca plegado).
export default {
	name: "DashboardSummaryTable",
	components: { ArrowDown },
	props: {
		// Mes focal "YYYY-MM": arma las etiquetas de las columnas.
		month: { type: String, required: true },
		rows: { type: Array, default: () => [] },
		isLoading: { type: Boolean, default: false },
		// Mes de inicio del reporte "YYYY-MM" (null = sin piso): explica los
		// «—» de las columnas que caen antes.
		floorMonth: { type: String, default: null },
		// Etiqueta de la celda superior izquierda (vacía por pedido de Steve,
		// 2026-10-06: la moneda es siempre CLP y no hace falta decirlo).
		cornerLabel: { type: String, default: "" },
		navigable: { type: Boolean, default: true },
		// Claves de los totales desplegados: sus cuentas se muestran.
		expandedRows: { type: Array, default: () => [] },
		// Tabs cuyas filas son clickeables (abren su drawer); null = todas
		// las que tengan tab destino.
		drillTabs: { type: Array, default: null },
	},
	data() {
		return { EMPTY_VALUE, skeletonRows: SKELETON_ROWS };
	},
	computed: {
		focal() {
			return moment(this.month, "YYYY-MM");
		},
		isCurrentMonth() {
			return this.focal.isSame(moment(), "month");
		},
		rowsByKey() {
			return Object.fromEntries(this.rows.map((row) => [row.key, row]));
		},
		// Profundidad de cada fila (0 = sin padre) siguiendo la cadena de padres.
		depthByKey() {
			const depth = {};
			this.rows.forEach((row) => {
				let level = 0;
				let parent = row.parentKey ? this.rowsByKey[row.parentKey] : null;
				while (parent) {
					level += 1;
					parent = parent.parentKey ? this.rowsByKey[parent.parentKey] : null;
				}
				depth[row.key] = level;
			});
			return depth;
		},
		// Las filas sin padre siempre; una hija sólo si cada padre de su
		// cadena está desplegado (plegar el total esconde también las cuentas
		// de sus líneas).
		visibleRows() {
			return this.rows.filter((row) => {
				let parentKey = row.parentKey;
				while (parentKey) {
					if (!this.expandedRows.includes(parentKey)) return false;
					const parent = this.rowsByKey[parentKey];
					parentKey = parent ? parent.parentKey : null;
				}
				return true;
			});
		},
		columns() {
			const label = (m) => {
				const text = m.locale("es").format("MMM YYYY").replace(".", "");
				return text.charAt(0).toUpperCase() + text.slice(1);
			};
			const focal = this.focal;
			return [
				{ key: "month", value: "month", label: label(focal.clone()), headClass: "head_focal", cellClass: "cell_focal", colClass: "col_value" },
				{ key: "prev_month", value: "prevMonth", label: label(focal.clone().subtract(1, "month")), cellClass: "cell_muted", colClass: "col_value" },
				{ key: "mom", delta: "mom", label: "Δ% MoM", headClass: "head_delta", cellClass: "cell_delta", colClass: "col_delta" },
				{ key: "prev_year_month", value: "prevYearMonth", label: label(focal.clone().subtract(1, "year")), cellClass: "cell_muted", colClass: "col_value" },
				{ key: "yoy", delta: "yoy", label: "Δ% YoY", headClass: "head_delta", cellClass: "cell_delta", colClass: "col_delta" },
				{ key: "ytd", value: "ytd", label: `YTD ${focal.year()}`, headClass: "head_focal", cellClass: "cell_focal", colClass: "col_value" },
				{ key: "prev_ytd", value: "prevYtd", label: `YTD ${focal.year() - 1}`, cellClass: "cell_muted", colClass: "col_value" },
				{ key: "ytd_delta", delta: "ytd", label: "Δ% YTD", headClass: "head_delta", cellClass: "cell_delta", colClass: "col_delta" },
			];
		},
		emptyTooltip() {
			if (!this.floorMonth) return "Sin datos para este período.";
			const floor = moment(this.floorMonth, "YYYY-MM").locale("es").format("MMMM YYYY");
			return `Sin datos: el reporte comienza en ${floor}.`;
		},
	},
	methods: {
		parseToMoney,
		parseToCompactMoney,
		formatPercent,
		deltaTone,
		formatDelta,
		isExpanded(row) {
			return this.expandedRows.includes(row.key);
		},
		depthOf(row) {
			return this.depthByKey[row.key] || 0;
		},
		// Nombre completo para el tooltip cuando no cabe en la columna fija.
		// Umbral en caracteres, conservador y según la sangría (180 px menos
		// padding y chevron dan ~20 caracteres a una fila plana, ~17 a una
		// línea bajo un total, ~14 a una cuenta bajo una línea). Mostrar el
		// tooltip de más es inofensivo; de menos dejaría un nombre cortado
		// sin forma de leerlo.
		labelTooltip(row) {
			const limit = [20, 17, 14][Math.min(this.depthOf(row), 2)];
			return row.label && row.label.length > limit ? row.label : "";
		},
		// Con `drillTabs`, sólo las filas de DINERO de esos tabs: una fila de
		// ratio («Gastos Totales %») apunta al tab de Gastos pero no tiene un
		// drawer que abrir.
		isClickable(row) {
			if (!this.navigable || !row.targetTab) return false;
			if (!this.drillTabs) return true;
			return Boolean(row.isMoney) && this.drillTabs.includes(row.targetTab);
		},
		onRowClick(row) {
			if (this.isClickable(row)) this.$emit("row-click", row);
		},
	},
};
</script>

<style lang="scss" scoped>
.summary_table_container {
	overflow-x: auto;
	border: 1px solid var(--report-border);
	border-radius: 12px;
	background: var(--report-bg);
	box-shadow: var(--report-shadow-card);
	transition: opacity 0.2s ease;

	&.is_refreshing {
		opacity: 0.55;
		pointer-events: none;
	}
}

// Anchos fijos de las columnas: la tabla mide exactamente su suma (830 px)
// y el layout del tab le da ese espacio, sin scroll horizontal.
$label-col: 180px;
$value-col: 88px;
$delta-col: 70px;

.summary_table {
	// Con `table-layout: fixed` el ancho tiene que ser explícito (con 100 %
	// la tabla se estiraba a toda la pista del layout y aplastaba los
	// gráficos). Las columnas del <colgroup> mandan: la de nombres mide
	// 180 px aunque una cuenta sea más larga (se recorta con «…» y el nombre
	// completo va en el tooltip), así la tabla no cambia de ancho al
	// desplegar.
	width: $label-col + 5 * $value-col + 3 * $delta-col;
	table-layout: fixed;
	border-collapse: separate;
	border-spacing: 0;
	font-size: 13px;
	color: var(--report-text);
	font-variant-numeric: tabular-nums;

	// Anchos fijos y angostos: la tabla define su propio ancho (~830 px) y el
	// layout del tab le da exactamente ese espacio, sin scroll horizontal
	// (Steve, 2026-10-07). Los montos van en formato compacto, así 88 px
	// alcanzan; los deltas son «−12%».
	.col_label { width: $label-col; }
	.col_value { width: $value-col; }
	.col_delta { width: $delta-col; }

	th, td {
		padding: 9px 10px;
		border-bottom: 1px solid var(--report-divider-soft);
		white-space: nowrap;
	}

	thead th {
		background: var(--report-primary);
		color: var(--report-bg);
		font-weight: 600;
		font-size: 12px;
		letter-spacing: 0.01em;
		border-bottom: none;
		position: sticky;
		top: 0;
		z-index: 2;
	}

	.header_corner {
		text-align: left;
		color: var(--report-accent-border);
		font-weight: 500;
	}

	// El mes focal es el hilo de la pantalla: su encabezado se destaca en la
	// tabla y la misma banda marca ese mes en los cuatro gráficos.
	.head_focal { color: var(--report-focus-col-text); }
	.head_delta { background: var(--report-delta-head-bg); }

	// Columna fija: el contenido que se desplaza pasa POR DEBAJO. Las celdas
	// toman el fondo de su fila (las filas de margen % conservan su swatch) y
	// la esquina del encabezado el del header — con `inherit` a secas quedaba
	// transparente (el `tr` del thead no tiene fondo) y las columnas se veían
	// a través de ella al hacer scroll.
	.sticky_column {
		position: sticky;
		left: 0;
		z-index: 1;
		text-align: left;
		background-color: inherit;
	}

	// Va DESPUÉS de .sticky_column (misma especificidad): la esquina es la
	// única celda fija en los dos ejes y tiene que quedar sobre los demás
	// encabezados (z-index 2), si no "Jul 2026" se dibuja encima de ella.
	.header_corner { background-color: var(--report-primary); z-index: 3; }

	tbody .sticky_column { box-shadow: inset -1px 0 0 var(--report-divider-soft); }

	tbody tr {
		background: var(--report-bg);

		&.is_emphasis .row_label,
		&.is_emphasis .cell_value { font-weight: 700; }

		&.navigable { cursor: pointer; }
		&.navigable:hover .row_label { text-decoration: underline; }
		&.navigable:focus-within .row_label { outline: 2px solid var(--report-accent-text); outline-offset: 2px; }
	}

	.row_label {
		font-weight: 500;
		color: var(--report-text);
	}

	// Nombre a la izquierda y chevron pegado a la derecha, centrado.
	.label_cell {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 6px;
		min-width: 0;
	}

	.row_label_text {
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.row_chevron {
		flex-shrink: 0;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 18px;
		height: 18px;
		padding: 0;
		border: 0;
		background: transparent;
		cursor: pointer;
		color: var(--report-text-muted-strong);

		&:focus-visible {
			outline: 2px solid var(--report-accent-border);
			outline-offset: 2px;
			border-radius: 4px;
		}
	}

	.row_chevron_icon {
		width: 12px;
		height: 12px;
		transition: transform 0.2s ease;

		&.is-rotated { transform: rotate(180deg); }
	}

	// Fila desplegada bajo su padre: sangría por profundidad (una línea bajo
	// un total, una cuenta bajo una línea), sin énfasis. Las cuentas de nivel
	// 3 llevan la banda gris humo del Resumen (`theme_row_account`); las
	// líneas bajo un total conservan su fondo.
	tbody tr.is_child {
		.row_label { font-weight: 400; }
		.cell_focal { background: transparent; }
	}

	tbody tr.depth_1 .row_label { padding-left: 26px; }
	tbody tr.depth_2 .row_label { padding-left: 44px; }

	.theme_row_account {
		background: var(--report-surface);
		color: var(--report-text-muted-strong);
	}

	.cell_focal { font-weight: 600; background: var(--report-focus-col); }
	.cell_muted { color: var(--report-text-muted); }
	.cell_delta { background: var(--report-delta-col-bg); }

	.delta {
		font-weight: 700;
		&.tone_favorable { color: var(--report-positive); }
		&.tone_unfavorable { color: var(--report-negative); }
		&.tone_neutral { color: var(--report-text-muted); }
	}

	.empty_value { color: var(--report-text-muted-soft); }

	// Filas de margen %: mismo swatch que el Resumen (viene en `row.theme`).
	.theme_row_white { background: var(--report-bg); }
	.theme_row_blue { background: var(--report-row-blue); color: var(--report-row-blue-text); }
	.theme_row_azure { background: var(--report-row-azure); color: var(--report-row-azure-text); }
	.theme_row_yellow { background: var(--report-row-yellow); color: var(--report-row-yellow-text); }
	.theme_row_purple { background: var(--report-row-purple); color: var(--report-row-purple-text); }
	.theme_row_orange { background: var(--report-row-orange); color: var(--report-row-orange-text); }

	// En las filas % el color de la fila manda sobre el gris de las columnas
	// de comparación; la celda focal mantiene su fondo pero toma el color.
	tr[class*="theme_row_"]:not(.theme_row_white) {
		.row_label, .cell_muted { color: inherit; font-weight: 600; }
		.cell_focal { background: transparent; }
	}

	.skeleton_bar {
		display: inline-block;
		width: 70%;
		height: 12px;
		border-radius: 6px;
		background: var(--report-divider-soft);
		animation: pulse 1.4s ease-in-out infinite;

		&.label_bar { width: 60%; }
	}
}

@keyframes pulse {
	0%, 100% { opacity: 1; }
	50% { opacity: 0.45; }
}

@media (prefers-reduced-motion: reduce) {
	.skeleton_bar { animation: none; }
	.summary_table_container { transition: none; }
}
</style>
